import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { Asset, Job, NodeLabel, Operator } from '@/types';
import { useQuery } from '@/utils/api';

/**
 * Fetches jobs for all member assets of an application in a single
 * DynamoDB multi-query request to /my. Returns a flat, deduplicated
 * job list covering every IP and WebApplication rolled up under the
 * application via HAS_MEMBER.
 *
 * When the asset is not an application, returns an empty idle result
 * so the caller can always invoke this hook unconditionally.
 */
export function useApplicationJobs(asset?: Asset) {
  const isApplication = asset?.class === 'application';
  const applicationKey = isApplication ? asset.key : asset?.group_key || '';
  const axios = useAxios();

  const { data: memberAssets = [], status: membersStatus } = useMy(
    {
      resource: 'asset',
      query: {
        node: {
          labels: ['Asset'] as NodeLabel[],
          relationships: [
            {
              label: 'HAS_MEMBER',
              source: {
                labels: ['Application'] as NodeLabel[],
                filters: [
                  {
                    field: 'key',
                    operator: '=' as Operator,
                    value: applicationKey,
                    not: false,
                  },
                ],
              },
            },
          ],
        },
      },
    },
    { enabled: Boolean(applicationKey) }
  );

  const { data: memberWebApps = [], status: webAppsStatus } = useMy(
    {
      resource: 'webapplication',
      query: {
        node: {
          labels: ['WebApplication'] as NodeLabel[],
          relationships: [
            {
              label: 'HAS_MEMBER',
              source: {
                labels: ['Application'] as NodeLabel[],
                filters: [
                  {
                    field: 'key',
                    operator: '=' as Operator,
                    value: applicationKey,
                    not: false,
                  },
                ],
              },
            },
          ],
        },
      },
    },
    { enabled: Boolean(applicationKey) }
  );

  const membersReady =
    membersStatus === 'success' && webAppsStatus === 'success';

  const latestAgentScan = useMemo(() => {
    if (!membersReady) return undefined;
    let latest: string | undefined;
    for (const member of memberAssets) {
      const ts = (member as Asset).lastAgentScan;
      if (ts && (!latest || ts > latest)) latest = ts;
    }
    for (const webApp of memberWebApps) {
      const ts = (webApp as Asset).lastAgentScan;
      if (ts && (!latest || ts > latest)) latest = ts;
    }
    if (asset?.lastAgentScan && (!latest || asset.lastAgentScan > latest)) {
      latest = asset.lastAgentScan;
    }
    return latest;
  }, [membersReady, memberAssets, memberWebApps, asset]);

  const jobKeys = useMemo(() => {
    if (!applicationKey || !asset || !membersReady) return [];
    const keys = new Set<string>();
    keys.add(`#job#${asset.group}#${asset.identifier}`);
    for (const member of memberAssets) {
      keys.add(`#job#${member.group}#${member.identifier}`);
    }
    for (const webApp of memberWebApps) {
      keys.add(`#job#${webApp.group}#${webApp.identifier}`);
    }
    return Array.from(keys);
  }, [applicationKey, asset, membersReady, memberAssets, memberWebApps]);

  const {
    data: jobs,
    status: jobsStatus,
    error: jobsError,
    refetch,
  } = useQuery<Job[]>({
    queryKey: ['APPLICATION_JOBS', applicationKey, jobKeys.join(',')],
    enabled: Boolean(applicationKey) && membersReady && jobKeys.length > 0,
    refetchInterval: 5000,
    defaultErrorMessage: 'Failed to fetch application jobs',
    queryFn: async () => {
      const params = new URLSearchParams();
      jobKeys.forEach(k => params.append('key', k));
      params.append('label', 'job');
      const { data } = await axios.get('/my', { params });

      // Multi-key returns an array of results; single-key returns direct
      const results = Array.isArray(data) ? data : [data];
      const allJobs: Job[] = results.flatMap(
        (r: Record<string, unknown>) => (r?.jobs as Job[] | undefined) ?? []
      );

      const seen = new Set<string>();
      return allJobs.filter(job => {
        if (seen.has(job.key)) return false;
        seen.add(job.key);
        return true;
      });
    },
  });

  return {
    data: jobs ?? ([] as Job[]),
    status: !applicationKey
      ? ('success' as const)
      : !membersReady
        ? ('pending' as const)
        : jobsStatus,
    error: jobsError ?? null,
    refetch,
    latestAgentScan,
  };
}
